import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTransactions } from '../context/TransactionContext';
import { useSettings } from '../context/SettingsContext';
import { TransactionSkeleton, PageSkeleton, StatsSkeleton } from '../components/SkeletonLoader';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { filterTransactionsByPeriod } from '../utils/dateFilters';

function Transactions() {
  const { transactions, loading, error, addTransaction, updateTransaction, deleteTransaction } = useTransactions();
  const { formatCurrency, categories, formatDate } = useSettings();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const filteredTransactions = useMemo(() => {
    return filterTransactionsByPeriod(transactions, selectedPeriod);
  }, [transactions, selectedPeriod]);
  const stats = useMemo(() => {
    const totalIncome = filteredTransactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = filteredTransactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const netBalance = totalIncome - totalExpenses;
    return {
      monthlyIncome: totalIncome,
      monthlyExpenses: totalExpenses,
      netBalance
    };
  }, [filteredTransactions]);


  const [newTransaction, setNewTransaction] = useState({
    description: '',
    category: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    type: 'expense'
  });





  const loadMoreRef = useRef();

  // Initialize loading state
  useEffect(() => {
    if (transactions.length > 0) {
      setIsInitialLoad(false);
    }
  }, [transactions]);



  const { displayedItems, hasMore, loading: scrollLoading, loadMore, progress } = useInfiniteScroll(filteredTransactions, 15);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !scrollLoading) {
          loadMore();
        }
      },
      { threshold: 1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, scrollLoading, loadMore]);



  // Show initial loading skeleton
  if (isInitialLoad && loading) {
    return <PageSkeleton />;
  }

  const handleAddTransaction = async () => {
    if (!newTransaction.description || !newTransaction.category || !newTransaction.amount) return;
    await addTransaction(newTransaction);
    setNewTransaction({
      description: '',
      category: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      type: 'expense'
    });
    setShowAddModal(false);
  };

  const startEdit = (transaction) => {
    setEditingTransaction({ ...transaction, amount: Math.abs(transaction.amount) });
    setShowAddModal(true);
  };

  const handleUpdateTransaction = async () => {
    if (!editingTransaction.description || !editingTransaction.category || !editingTransaction.amount) return;
    await updateTransaction(editingTransaction);
    setEditingTransaction(null);
    setShowAddModal(false);
  };

  const { monthlyIncome: totalIncome, monthlyExpenses: totalExpenses, netBalance } = stats;

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div className="transaction-header" style={{ marginBottom: '0.5rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
            Transactions
          </h1>
          <div className="transaction-actions">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="form-input period-selector"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              Add Transaction
            </button>
          </div>
        </div>
        <p style={{ color: 'var(--gray-500)' }}>
          Manage your transactions
        </p>
      </div>

      {/* Summary Cards */}
      {loading && transactions.length === 0 ? (
        <StatsSkeleton />
      ) : (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-title">Total Income</span>
              <span className="stat-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="m16 6 2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg></span>
            </div>
            <div className="stat-value" style={{ color: 'var(--income-green)' }}>{formatCurrency(totalIncome)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-title">Total Expenses</span>
              <span className="stat-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="m16 18 2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z"/></svg></span>
            </div>
            <div className="stat-value" style={{ color: 'var(--expense-red)' }}>{formatCurrency(totalExpenses)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-title">Net Balance</span>
              <span className="stat-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg></span>
            </div>
            <div className="stat-value" style={{ color: netBalance >= 0 ? 'var(--income-green)' : 'var(--expense-red)' }}>
              {formatCurrency(netBalance)}
            </div>

          </div>
        </div>
      )}



      {/* Transactions List */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
            Transactions ({filteredTransactions.length})
          </h3>
          {progress > 0 && progress < 100 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
              <div style={{ 
                width: '60px', 
                height: '4px', 
                background: '#e5e7eb', 
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: '#3b82f6',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <span>{Math.round(progress)}%</span>
            </div>
          )}
        </div>
        
        {loading ? (
          <div>
            {[...Array(5)].map((_, i) => <TransactionSkeleton key={i} />)}
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
            {error}
          </div>
        ) : displayedItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
            No transactions found
          </div>
        ) : (
          <div>
            {displayedItems.map((transaction, index) => (
              <div 
                key={transaction._id} 
                className="transaction-item"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem 0',
                  borderBottom: '1px solid var(--gray-200)',
                  animationDelay: `${index * 50}ms`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: transaction.amount > 0 ? '#dcfce7' : '#fee2e2'
                  }}>
                    {transaction.amount > 0 ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--income-green)">
                        <path d="M7 14l5-5 5 5z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--expense-red)">
                        <path d="M7 10l5 5 5-5z"/>
                      </svg>
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
                      {transaction.description}
                    </div>
                    <div style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                      {transaction.category} {formatDate(transaction.date)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    fontWeight: '700',
                    fontSize: '1.125rem',
                    color: transaction.amount > 0 ? 'var(--income-green)' : 'var(--expense-red)'
                  }}>
                    {formatCurrency(transaction.amount)}
                  </div>
                  <div className="transaction-item-actions">
                    <button
                      onClick={() => startEdit(transaction)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--trust-blue)' }}
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTransaction(transaction._id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--danger-red)' }}
                      title="Delete"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {scrollLoading && (
              <div>
                {[...Array(3)].map((_, i) => <TransactionSkeleton key={`loading-${i}`} delay={i * 100} />)}
              </div>
            )}
            
            {hasMore && (
              <div ref={loadMoreRef} style={{ height: '20px', margin: '1rem 0' }} />
            )}
            
            {!hasMore && displayedItems.length > 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '1rem', 
                color: 'var(--gray-500)', 
                fontSize: '0.875rem',
                borderTop: '1px solid var(--gray-200)',
                marginTop: '1rem'
              }}>
                All {filteredTransactions.length} transactions loaded
              </div>
            )}
            
            {displayedItems.length === 0 && !loading && (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem 1rem',
                color: 'var(--gray-500)'
              }}>
                <h3 style={{ marginBottom: '0.5rem' }}>No transactions found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Transaction Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal" style={{
            background: 'var(--white)',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
            </h3>
            
            <div className="form-grid" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  value={editingTransaction ? editingTransaction.type : newTransaction.type}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingTransaction) {
                      setEditingTransaction(prev => ({ ...prev, type: value, category: '' }));
                    } else {
                      setNewTransaction(prev => ({ ...prev, type: value, category: '' }));
                    }
                  }}
                  className="form-input"
                >
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  placeholder="Enter description"
                  value={editingTransaction ? editingTransaction.description : newTransaction.description}
                  onChange={(e) => {
                    if (editingTransaction) {
                      setEditingTransaction(prev => ({ ...prev, description: e.target.value }));
                    } else {
                      setNewTransaction(prev => ({ ...prev, description: e.target.value }));
                    }
                  }}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  value={editingTransaction ? editingTransaction.category : newTransaction.category}
                  onChange={(e) => {
                    if (editingTransaction) {
                      setEditingTransaction(prev => ({ ...prev, category: e.target.value }));
                    } else {
                      setNewTransaction(prev => ({ ...prev, category: e.target.value }));
                    }
                  }}
                  className="form-input"
                >
                  <option value="">Select category</option>
                  {categories[editingTransaction ? editingTransaction.type : newTransaction.type].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Amount</label>
                <input
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  value={editingTransaction ? editingTransaction.amount : newTransaction.amount}
                  onChange={(e) => {
                    if (editingTransaction) {
                      setEditingTransaction(prev => ({ ...prev, amount: e.target.value }));
                    } else {
                      setNewTransaction(prev => ({ ...prev, amount: e.target.value }));
                    }
                  }}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  value={editingTransaction ? editingTransaction.date : newTransaction.date}
                  onChange={(e) => {
                    if (editingTransaction) {
                      setEditingTransaction(prev => ({ ...prev, date: e.target.value }));
                    } else {
                      setNewTransaction(prev => ({ ...prev, date: e.target.value }));
                    }
                  }}
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="transaction-actions modal-actions" style={{ justifyContent: 'flex-end', marginTop: '2rem' }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingTransaction(null);
                  setNewTransaction({
                    description: '',
                    category: '',
                    amount: '',
                    date: new Date().toISOString().split('T')[0],
                    type: 'expense'
                  });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={editingTransaction ? handleUpdateTransaction : handleAddTransaction}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Processing...' : (editingTransaction ? 'Update' : 'Add')} Transaction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Transactions;